import React from 'react';
import { Box, Typography, Divider, Grid, Button, CircularProgress, Alert } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { EnvioNacionalForm } from './EnvioNacionalModal.types';

interface Props {
  form: EnvioNacionalForm;
  onEditarPaso: (paso: number) => void;
  precioCotizado?: number | null;
  isLoadingCotizacion?: boolean;
  errorCotizacion?: string | null;
  esColombia?: boolean;
}

const SectionTitle: React.FC<{ title: string; onEditar: () => void }> = ({ title, onEditar }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
    <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', fontWeight: 700, color: 'text.primary' }}>
      {title}
    </Typography>
    <Divider sx={{ flex: 1 }} />
    <Button
      size="small"
      variant="text"
      color="primary"
      onClick={onEditar}
      sx={{ whiteSpace: 'nowrap', fontWeight: 600, textTransform: 'none', minWidth: 'auto' }}
    >
      Editar
    </Button>
  </Box>
);

const Campo: React.FC<{ label: string; valor: string }> = ({ label, valor }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {valor || '—'}
    </Typography>
  </Grid>
);

export const ResumenEnvio: React.FC<Props> = ({
  form,
  onEditarPaso,
  precioCotizado,
  isLoadingCotizacion,
  errorCotizacion,
  esColombia = false,
}) => {
  const { remitente, destinatario, producto } = form;

  const renderPersona = (datos: typeof remitente, titulo: string, paso: number) => (
    <Box>
      <SectionTitle title={titulo} onEditar={() => onEditarPaso(paso)} />
      <Grid container spacing={2}>
        <Campo label="Nombre" valor={datos.nombre} />
        <Campo label="Teléfono" valor={`+${datos.indicativoPais} ${datos.numeroCelular}`} />
        <Campo label="Correo" valor={datos.correo} />
        {esColombia ? (
          <>
            {datos.tipoDocumento && datos.identificacion && (
              <Campo label={datos.tipoDocumento} valor={datos.identificacion} />
            )}
            <Campo label="Ciudad" valor={datos.ciudadLabel ?? datos.municipio} />
            <Campo label="Dirección" valor={datos.direccion} />
          </>
        ) : (
          <>
            <Campo label="Código postal" valor={datos.codigoPostal} />
            <Campo label="Municipio" valor={datos.municipio} />
            <Campo label="Colonia" valor={datos.colonia} />
            <Campo label="Dirección" valor={datos.direccion} />
          </>
        )}
        {datos.detalleDireccion && (
          <Campo label="Detalle dirección" valor={datos.detalleDireccion} />
        )}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      {renderPersona(remitente, 'Remitente', 0)}
      {renderPersona(destinatario, 'Destinatario', 1)}

      {/* Producto */}
      <Box>
        <SectionTitle title="Producto" onEditar={() => onEditarPaso(2)} />
        <Grid container spacing={2}>
          <Campo label="Tipo" valor={producto.tipo === 'paquete' ? 'Paquetes' : 'Documentos'} />
          {!esColombia && <Campo label="Valor declarado" valor={`$${producto.valorDeclarado}`} />}
        </Grid>

        {producto.tipo === 'paquete' && producto.paquetes.map((paquete, idx) => (
          <Box key={`paquete-${paquete.contenido}-${idx}`} sx={{ mt: 2 }}>
            {producto.paquetes.length > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                Paquete {idx + 1}
              </Typography>
            )}
            <Grid container spacing={2}>
              <Campo label="Largo (cm)" valor={paquete.largo} />
              <Campo label="Ancho (cm)" valor={paquete.ancho} />
              <Campo label="Alto (cm)" valor={paquete.alto} />
              <Campo label="Peso (kg)" valor={paquete.peso} />
              <Campo label="Cantidad" valor={String(paquete.cantidad)} />
              <Campo label="Contenido" valor={paquete.contenido} />
              {esColombia && paquete.valorDeclarado && (
                <Campo label="Valor declarado" valor={`$${Number(paquete.valorDeclarado).toLocaleString('es-CO')}`} />
              )}
            </Grid>
          </Box>
        ))}

        {producto.tipo === 'documento' && (
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Campo label="Peso" valor={`${producto.pesoDocumento} Kg`} />
            <Campo label="Contenido" valor={producto.contenidoDocumento} />
            {esColombia && producto.referenciaDocumento && (
              <Campo label="Referencia" valor={producto.referenciaDocumento} />
            )}
            {esColombia && <Campo label="Valor declarado" valor={`$${Number(producto.valorDeclarado).toLocaleString('es-CO')}`} />}
          </Grid>
        )}
      </Box>

      {/* Precio estimado */}
      <Box sx={{ mt: 1 }}>
        {isLoadingCotizacion && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Calculando precio del envío...</Typography>
          </Box>
        )}
        {errorCotizacion && !isLoadingCotizacion && (
          <Alert severity="warning" sx={{ mt: 1 }}>{errorCotizacion}</Alert>
        )}
        {precioCotizado !== null && precioCotizado !== undefined && !isLoadingCotizacion && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: 2, borderRadius: 2,
            backgroundColor: 'primary.main',
            color: 'white',
          }}>
            <LocalShippingIcon fontSize="small" />
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Precio estimado del envío
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                ${precioCotizado.toLocaleString(esColombia ? 'es-CO' : 'es-MX')}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

    </Box>
  );
};
